import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    confirmPasswordResetRequest,
    clearPasswordResetState,
} from "../../../redux/actions/emailActions";
import "./login.css";
import "./ResetPasswordPage.css";

const ResetPasswordPage = ({
                               resetLoading,
                               resetConfirmMessage,
                               resetError,
                               confirmPasswordReset,
                               clearResetState,
                           }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
    const [showNew, setShowNew]         = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        return () => { clearResetState(); };
    }, []);

    useEffect(() => {
        const isRTL = i18n.language === "ar";
        document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
        document.documentElement.setAttribute("lang", i18n.language);
        document.body.classList.toggle("rtl-layout", isRTL);
    }, [i18n.language]);

    useEffect(() => {
        if (resetConfirmMessage) {
            const timer = setTimeout(() => navigate("/"), 2000);
            return () => clearTimeout(timer);
        }
    }, [resetConfirmMessage, navigate]);

    const passwordMismatch = useMemo(
        () => form.confirmPassword && form.newPassword !== form.confirmPassword,
        [form.newPassword, form.confirmPassword]
    );

    const passwordHints = useMemo(() => {
        const p = form.newPassword || "";
        return {
            length:  p.length >= 8,
            upper:   /[A-Z]/.test(p),
            lower:   /[a-z]/.test(p),
            digit:   /[0-9]/.test(p),
            special: /[^A-Za-z0-9]/.test(p),
        };
    }, [form.newPassword]);

    const passwordValid = Object.values(passwordHints).every(Boolean);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!token || !passwordValid || passwordMismatch) return;
        confirmPasswordReset(token, form.newPassword, form.confirmPassword);
    };

    /* ── Icônes ── */
    const IconLock = ({ color = "#aab4cb" }) => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill={color}>
            <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"/>
        </svg>
    );

    const IconEyeOpen = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="#aab4cb"/>
        </svg>
    );

    const IconEyeOff = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.73 7C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27Z" fill="#aab4cb"/>
        </svg>
    );

    const IconAlert = ({ color }) => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill={color}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
    );

    const IconCheck = () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="#166534">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
    );

    return (
        <>
            <div className={`login-container rpp-page ${i18n.language === "ar" ? "rtl rtl-bg" : i18n.language?.startsWith("en") ? "en-bg" : ""}`}>
                <main className="login-box rpp-card">
                    {/* Icône déco */}
                    <div className="rpp-icon-wrap">
                        <div className="rpp-icon-ring">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="#2a5ca9">
                                <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"/>
                            </svg>
                        </div>
                    </div>

                    <h2>{t("Réinitialisation")}</h2>
                    <p>{t("Définissez un nouveau mot de passe sécurisé.")}</p>

                    {/* Alerte token manquant */}
                    {!token && (
                        <div className="rpp-alert error">
                            <IconAlert color="#c0392b" />
                            {t("Token invalide ou manquant.")}
                        </div>
                    )}

                    {/* Succès */}
                    {resetConfirmMessage && (
                        <div className="rpp-alert success">
                            <IconCheck />
                            {resetConfirmMessage}
                        </div>
                    )}

                    {/* Erreur API */}
                    {resetError && (
                        <div className="rpp-alert error">
                            <IconAlert color="#c0392b" />
                            {resetError}
                        </div>
                    )}

                    <form className="rpp-form" onSubmit={handleSubmit}>

                        {/* Nouveau mot de passe */}
                        <div className="rpp-input-wrap">
                            <span className="rpp-input-icon">
                                <IconLock />
                            </span>
                            <input
                                type={showNew ? "text" : "password"}
                                name="newPassword"
                                placeholder={t("Nouveau mot de passe")}
                                value={form.newPassword}
                                onChange={handleChange}
                                disabled={resetLoading}
                            />
                            <button
                                type="button"
                                className="rpp-toggle-pw"
                                onClick={() => setShowNew(!showNew)}
                                disabled={resetLoading}
                            >
                                {showNew ? <IconEyeOff /> : <IconEyeOpen />}
                            </button>
                        </div>

                        {/* Confirmation */}
                        <div className="rpp-input-wrap">
                            <span className="rpp-input-icon">
                                <IconLock />
                            </span>
                            <input
                                type={showConfirm ? "text" : "password"}
                                name="confirmPassword"
                                placeholder={t("Confirmer le mot de passe")}
                                value={form.confirmPassword}
                                onChange={handleChange}
                                disabled={resetLoading}
                            />
                            <button
                                type="button"
                                className="rpp-toggle-pw"
                                onClick={() => setShowConfirm(!showConfirm)}
                                disabled={resetLoading}
                            >
                                {showConfirm ? <IconEyeOff /> : <IconEyeOpen />}
                            </button>
                        </div>

                        {/* Règles */}
                        <div className="rpp-rules">
                            <div className={passwordHints.length  ? "ok" : ""}>• {t("8 caractères minimum")}</div>
                            <div className={passwordHints.upper   ? "ok" : ""}>• {t("1 majuscule")}</div>
                            <div className={passwordHints.lower   ? "ok" : ""}>• {t("1 minuscule")}</div>
                            <div className={passwordHints.digit   ? "ok" : ""}>• {t("1 chiffre")}</div>
                            <div className={passwordHints.special ? "ok" : ""}>• {t("1 caractère spécial")}</div>
                        </div>

                        {/* Mismatch */}
                        {passwordMismatch && (
                            <div className="rpp-inline-error">
                                <IconAlert color="#c0392b" />
                                {t("Les mots de passe ne correspondent pas.")}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={!token || resetLoading || passwordMismatch || !passwordValid}
                        >
                            {resetLoading ? t("Validation en cours...") : t("Valider le mot de passe")}
                        </button>
                    </form>

                    {/*<div className="rpp-back-link">*/}
                    {/*    <Link to="/login">← Retour à la connexion</Link>*/}
                    {/*</div>*/}
                </main>
            </div>

            <div className="language-selector">
                {["fr", "en", "ar"].map((lng, i, languages) => (
                    <React.Fragment key={lng}>
                        <button
                            type="button"
                            onClick={() => i18n.changeLanguage(lng)}
                            className={i18n.language === lng ? "active" : ""}
                        >
                            {lng.toUpperCase()}
                        </button>
                        {i < languages.length - 1 && <span className="language-separator">|</span>}
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    resetLoading:         state.emails && state.emails.resetLoading        || false,
    resetConfirmMessage:  state.emails && state.emails.resetConfirmMessage || "",
    resetError:           state.emails && state.emails.resetError          || null,
});

const mapDispatchToProps = (dispatch) => ({
    confirmPasswordReset: (token, newPassword, confirmPassword) =>
        dispatch(confirmPasswordResetRequest(token, newPassword, confirmPassword)),
    clearResetState: () => dispatch(clearPasswordResetState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordPage);
